import * as React from 'react';
import { validateFields, toIFieldData } from '../helpers/dataHelper';
import {
  ICreds,
  IMember,
  IFieldData,
  IFieldChangeData,
  IAllianceData,
  ISignIn,
  signInPopupType,
  MembersPopupType,
  TestWebhooksPopupType,
  IMembersData,
  IPopupData,
  IResetPassword,
  DeleteAllDataPopupType,
} from '../constants/models';

export const reducer = (state: any, action: any) => {
  switch (action.type) {
    case 'cleanData':
      return {
        ...action.payload,
      };
    case 'credentials':
      return {
        ...state,
        credentials: action.payload,
      };
    case 'members':
      return {
        ...state,
        members: action.payload,
      };
    case 'fieldChange':
      if (
        action.payload.fieldName === 'userName' ||
        action.payload.fieldName === 'password' ||
        action.payload.fieldName === 'confirmPassword'
      ) {
        return {
          ...state,
          signIn: {
            ...state.signIn,
            [action.payload.fieldName]: { value: action.payload.value, error: action.payload.error },
          },
        };
      } else if (
        action.payload.fieldName === 'memberFirstName' ||
        action.payload.fieldName === 'memberLastName' ||
        action.payload.fieldName === 'email'
      ) {
        return {
          ...state,
          addMember: {
            ...state.addMember,
            [action.payload.fieldName]: { value: action.payload.value, error: action.payload.error },
          },
        };
      } else if (action.payload.fieldName === 'resetPasswordUser') {
        return {
          ...state,
          resetPassword: {
            ...state.resetPassword,
            [action.payload.fieldName]: {
              fieldName: action.payload.fieldName,
              value: action.payload.value,
              error: action.payload.error,
            },
          },
        };
      }
      break;
    case 'membersPopup':
      return {
        ...state,
        membersPopup: {
          ...state.membersPopup,
          status: action.payload,
        },
      };
    case 'membersPopupMessage':
      return {
        ...state,
        membersPopup: action.payload,
      };
    case 'membersNewData':
      return {
        ...state,
        members: [...state.members, action.payload],
      };
    case 'allianceNewData':
      return {
        ...state,
        allianceData: action.payload,
      };
    case 'resetPasswordPopupSet':
      return {
        ...state,
        resetPassword: {
          ...state.resetPassword,
          resetPasswordPopupStatus: action.payload,
        },
      };
    case 'deleteAllDataPopup':
      return {
        ...state,
        deleteAllDataPopup: {
          ...state.deleteAllDataPopup,
          status: action.payload,
        },
      };
    case 'cleanAddMember':
      return {
        ...state,
        addMember: {
          memberFirstName: {
            value: '',
            error: '',
          },
          memberLastName: {
            value: '',
            error: '',
          },
          email: {
            value: '',
            error: '',
          },
        },
      };
    case 'testWebhookPopup':
      return {
        ...state,
        testWebhookPopup: action.payload,
      };
    case 'signInPopup':
      return {
        ...state,
        signIn: {
          ...state.signIn,
          signInPopup: action.payload,
        },
      };
    default:
      return state;
  }
};

interface IAppContext {
  credentials: ICreds;
  membersPopup: IPopupData;
  deleteAllDataPopup: IPopupData;
  members: Array<IMembersData>;
  signIn: ISignIn;
  resetPassword: IResetPassword;
  addMember: IMember;
  allianceData: IAllianceData;
  testWebhookPopup: IPopupData;
  setCredentials: (newData: ICreds) => void;
  setMembers: (newData: IMember) => void;
  cleanUserData: () => void;
  onFieldChange: (fieldChangeData: IFieldChangeData) => void;
  setAddMembersPopup: (popupStatus: string) => void;
  setPopupMessage: (newMember: string, popupStatus: string) => void;
  setMembersList: (newMember: IMembersData) => void;
  setAllianceData: (newMember: IAllianceData) => void;
  setResetPasswordPopupStatus: () => void;
  cleanAddedMemberData: () => void;
  setTestWebhookPopup: (popupStatus: string) => void;
  setSignInPopup: (popupStatus: string) => void;
  setDeleteAllDataPopup: (popupStatus: string) => void;
  deleteAllData: () => void;
}

const initialState: IAppContext = {
  credentials: {
    userName: '',
    userToken: '',
  },
  allianceData: {
    userFirstName: '',
    userLastName: '',
    allianceName: '',
    userRole: '',
  },
  signIn: {
    userName: {
      value: '',
      error: '',
    },
    password: {
      value: '',
      error: '',
    },
    confirmPassword: {
      value: '',
      error: '',
    },
    signInPopup: {
      title: '',
      description: '',
      status: '',
    },
  },
  addMember: {
    memberFirstName: {
      value: '',
      error: '',
    },
    memberLastName: {
      value: '',
      error: '',
    },
    email: {
      value: '',
      error: '',
    },
  },
  members: [],
  membersPopup: {
    title: '',
    description: '',
    status: '',
  },
  deleteAllDataPopup: {
    title: '',
    description: '',
    status: '',
  },
  testWebhookPopup: {
    title: '',
    description: '',
    status: '',
  },
  resetPassword: {
    resetPasswordUser: {
      fieldName: 'resetPasswordUser',
      value: '',
      error: '',
    },
    resetPasswordPopupStatus: false,
  },

  setCredentials: () => {},
  setMembers: () => {},
  cleanUserData: () => {},
  onFieldChange: () => {},
  setAddMembersPopup: () => {},
  setPopupMessage: () => {},
  setMembersList: () => {},
  setAllianceData: () => {},
  setResetPasswordPopupStatus: () => {},
  cleanAddedMemberData: () => {},
  setTestWebhookPopup: () => {},
  setSignInPopup: () => {},
  setDeleteAllDataPopup: () => {},
  deleteAllData: () => {},
};

export const AppContext = React.createContext<IAppContext>(initialState);

export const AppContextProvider = (props: any) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  const setCredentials = (newData: ICreds) => {
    dispatch({
      type: 'credentials',
      payload: newData,
    });
  };

  const setMembers = (newData: IMember) => {
    dispatch({
      type: 'members',
      payload: newData,
    });
  };

  const cleanUserData = () => {
    dispatch({
      type: 'cleanData',
      payload: initialState,
    });
  };

  const onFieldChange = (fieldChangeData: IFieldChangeData): void => {
    let fieldData: IFieldData;

    if (fieldChangeData.passValidation) {
      fieldData = toIFieldData(fieldChangeData);
    } else {
      if (fieldChangeData.fieldName === 'confirmPassword')
        fieldChangeData.payload = { password: state.signIn.password.value };

      fieldData = validateFields(fieldChangeData);
    }

    dispatch({
      type: 'fieldChange',
      payload: fieldData,
    });
  };

  // This is for open and close add members popup
  const setAddMembersPopup = (popupStatus: MembersPopupType | string) => {
    dispatch({
      type: 'membersPopup',
      payload: popupStatus,
    });
  };

  // This is for open and close add deleteAllData popup
  const setDeleteAllDataPopup = (popupStatus: DeleteAllDataPopupType | string) => {
    dispatch({
      type: 'deleteAllDataPopup',
      payload: popupStatus,
    });
  };

  const deleteAllData = () => {
    dispatch({
      type: 'deleteAllData',
      payload: initialState,
    });
  };

  // This is for setting fail & success popup message
  const setTestWebhookPopup = (popupStatus: TestWebhooksPopupType | string) => {
    let newMessageData;
    if (popupStatus === TestWebhooksPopupType.testWebhookSuccess) {
      newMessageData = {
        title: 'Success',
        description: 'Webhook test passed',
        status: TestWebhooksPopupType.testWebhookSuccess,
      };
    } else if (popupStatus === TestWebhooksPopupType.testWebhookFail) {
      newMessageData = {
        title: 'Failed',
        description: 'Sorry, Could send webhook data',
        status: TestWebhooksPopupType.testWebhookFail,
      };
    } else {
      newMessageData = { title: '', description: '', status: '' };
    }
    dispatch({
      type: 'testWebhookPopup',
      payload: newMessageData,
    });
  };

  const setSignInPopup = (popupStatus: signInPopupType | string) => {
    let newMessageData;
    if (popupStatus === signInPopupType.signInFail) {
      newMessageData = {
        title: 'Login failed',
        description: 'The user name or password that you entered does not match our records.',
        status: signInPopupType.signInFail,
      };
    } else if (popupStatus === signInPopupType.signInLocked) {
      newMessageData = {
        title: 'Login failed',
        description: 'The account you are using is locked.',
        status: signInPopupType.signInFail,
      };
    } else if (popupStatus === signInPopupType.resetPasswordFailedLocked) {
      newMessageData = {
        title: 'Reset password failed',
        description: 'The account you are trying to reset is locked.',
        status: signInPopupType.signInFail,
      };
    } else if (popupStatus === signInPopupType.resetPasswordFailedGeneral) {
      newMessageData = {
        title: 'Reset password failed',
        description: 'The account you entered does not exist on our records, pleace check your email.',
        status: signInPopupType.signInFail,
      };
    } else if (popupStatus === signInPopupType.resetPasswordSuccess) {
      newMessageData = {
        title: 'Email sent',
        description: 'Reset link sent to your email',
        status: signInPopupType.resetPasswordSuccess,
      };
    } else {
      newMessageData = { title: '', description: '', status: '' };
    }
    dispatch({
      type: 'signInPopup',
      payload: newMessageData,
    });
  };

  // This is for setting fail & success popup message
  const setPopupMessage = (memberName: string, popupStatus: string) => {
    let newMessageData;
    if (popupStatus === 'addMemberSuccess') {
      newMessageData = { title: memberName, description: 'Member invitation sent!', status: popupStatus };
    } else {
      newMessageData = { title: memberName, description: 'Member invitation failed', status: popupStatus };
    }
    dispatch({
      type: 'membersPopupMessage',
      payload: newMessageData,
    });
  };

  const setMembersList = (newMember: IMembersData) => {
    dispatch({
      type: 'membersNewData',
      payload: newMember,
    });
  };

  const setAllianceData = (newData: IAllianceData) => {
    dispatch({
      type: 'allianceNewData',
      payload: newData,
    });
  };

  const setResetPasswordPopupStatus = (currentStatus: boolean) => {
    dispatch({
      type: 'resetPasswordPopupSet',
      payload: !currentStatus,
    });
  };

  const cleanAddedMemberData = () => {
    dispatch({
      type: 'cleanAddMember',
      payload: '',
    });
  };

  return (
    <AppContext.Provider
      value={{
        ...state,
        setCredentials,
        setMembers,
        cleanUserData,
        onFieldChange,
        setAddMembersPopup,
        setPopupMessage,
        setMembersList,
        setAllianceData,
        setResetPasswordPopupStatus,
        cleanAddedMemberData,
        setTestWebhookPopup,
        setSignInPopup,
        setDeleteAllDataPopup,
        deleteAllData,
      }}
    >
      {props.children}
    </AppContext.Provider>
  );
};
